import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  HeadphonesIcon, 
  Code, 
  Database, 
  Cloud, 
  TestTube,
  Presentation,
  Download,
  Eye,
  Clock,
  DollarSign,
  TrendingUp,
  Shield
} from 'lucide-react';

interface Presentation {
  id: string;
  title: string;
  audience: string;
  icon: React.ReactNode;
  duration: string;
  keyMessage: string;
  slides: Slide[];
  color: string;
}

interface Slide {
  title: string;
  content: string[];
  type: 'text' | 'comparison' | 'metrics' | 'code';
}

export function StressTestingPresentations() {
  const [selectedPresentation, setSelectedPresentation] = useState<string>('management');

  const presentations: Presentation[] = [
    {
      id: 'management',
      title: 'Business Risk Mitigation Through Database Stress Testing',
      audience: 'Costco Management',
      icon: <Users className="w-5 h-5" />,
      duration: '15 min',
      keyMessage: 'Prevent $2.3M/hour revenue losses through proactive database testing',
      color: 'bg-shiftleft-500',
      slides: [
        {
          title: 'Executive Summary',
          type: 'metrics',
          content: [
            'The Gap: JMeter tests APIs, but 80% of performance issues occur at the database layer',
            'The Risk: $2.3M per hour revenue loss during peak holiday outages (industry average)',
            'The Solution: Proactive stored procedure stress testing prevents database bottlenecks',
            'ROI: 15:1 return on investment through outage prevention'
          ]
        },
        {
          title: 'Why API Testing Isn\'t Enough',
          type: 'comparison',
          content: [
            'JMeter tests the "front door" (APIs) but misses the "engine room" (database)',
            'Real customer scenarios create database contention patterns JMeter can\'t simulate',
            'Black Friday 2023: 67% of retail outages were database-related, not API-related'
          ]
        },
        {
          title: 'Business Impact',
          type: 'metrics',
          content: [
            'Revenue Protection: Prevent $2.3M/hour losses during peak periods',
            'Customer Experience: Eliminate checkout delays and cart abandonment',
            'Competitive Advantage: 99.99% uptime while competitors struggle',
            'Brand Protection: Avoid negative social media during critical sales periods'
          ]
        },
        {
          title: 'Investment vs. Risk',
          type: 'comparison',
          content: [
            'Stress Testing Investment: $50K implementation + $20K/year maintenance',
            'Single Outage Cost: $2.3M/hour + brand damage + customer churn',
            'Break-even: Preventing just 1 hour of downtime pays for 3+ years'
          ]
        }
      ]
    },
    {
      id: 'support',
      title: 'Reducing Customer Pain Points Through Proactive Testing',
      audience: 'Customer Support',
      icon: <HeadphonesIcon className="w-5 h-5" />,
      duration: '12 min',
      keyMessage: '60% reduction in performance-related support tickets',
      color: 'bg-green-500',
      slides: [
        {
          title: 'Customer Impact Focus',
          type: 'text',
          content: [
            'Current Pain: Customers experience slow checkouts, failed transactions, inventory errors',
            'Root Cause: Database bottlenecks during peak periods that JMeter doesn\'t catch',
            'Solution: Stress test stored procedures to prevent customer-facing issues'
          ]
        },
        {
          title: 'Support Ticket Analysis',
          type: 'metrics',
          content: [
            '73% of customer complaints during peak periods relate to:',
            '• Slow checkout processes',
            '• Inventory discrepancies', 
            '• Order processing delays',
            'These issues trace back to database performance, not API performance'
          ]
        },
        {
          title: 'Customer Experience Metrics',
          type: 'comparison',
          content: [
            'Before Stress Testing: 15-second average checkout time during peak',
            'After Stress Testing: 3-second average checkout time (industry benchmark)',
            'Customer Satisfaction: 40% improvement in CSAT scores',
            'Support Volume: 60% reduction in performance-related tickets'
          ]
        }
      ]
    },
    {
      id: 'developers',
      title: 'Why JMeter Can\'t Test What We Need to Test',
      audience: 'SQL Developers',
      icon: <Code className="w-5 h-5" />,
      duration: '18 min',
      keyMessage: 'JMeter tests APIs, but misses 80% of database performance issues',
      color: 'bg-purple-500',
      slides: [
        {
          title: 'Technical Reality Check',
          type: 'comparison',
          content: [
            'JMeter Tests: HTTP requests, API response times, throughput',
            'Missing Layer: Database concurrency, locking, deadlocks, resource contention',
            'The Gap: 80% of performance issues happen below the API layer'
          ]
        },
        {
          title: 'What JMeter Misses',
          type: 'code',
          content: [
            '-- JMeter can\'t test this scenario:',
            '-- 500 concurrent users hitting usp_al_import_850',
            '-- Each with different parameter combinations',
            '-- Creating tempdb contention and blocking chains',
            '-- While inventory updates are happening simultaneously'
          ]
        },
        {
          title: 'Real-World Scenarios JMeter Can\'t Simulate',
          type: 'text',
          content: [
            'Parameter Sniffing: Different execution plans based on parameter values',
            'Deadlock Cascades: Multiple procedures competing for same resources',
            'TempDB Contention: Heavy sorting/grouping operations under load',
            'Lock Escalation: Row locks escalating to table locks under pressure'
          ]
        }
      ]
    },
    {
      id: 'dbas',
      title: 'Database Performance Under Real Load Conditions',
      audience: 'SQL DBAs',
      icon: <Database className="w-5 h-5" />,
      duration: '20 min',
      keyMessage: 'Get visibility into database internals that JMeter cannot provide',
      color: 'bg-orange-500',
      slides: [
        {
          title: 'DBA Perspective',
          type: 'text',
          content: [
            'JMeter Limitation: Tests application layer, not database internals',
            'DBA Need: Understand database behavior under concurrent stored procedure execution',
            'Missing Insights: Wait statistics, blocking chains, resource utilization patterns'
          ]
        },
        {
          title: 'What DBAs Need to Know',
          type: 'text',
          content: [
            'How do our stored procedures behave with 1000+ concurrent executions?',
            'Which procedures cause blocking chains during peak periods?',
            'What are the tempdb usage patterns under real load?',
            'Where are the deadlock hotspots in our procedure calls?'
          ]
        },
        {
          title: 'Monitoring Gaps',
          type: 'code',
          content: [
            '-- JMeter shows: API response time = 200ms',
            '-- Reality: Database wait time = 15 seconds',
            '-- Hidden: Blocking chain of 47 sessions',
            '-- Missing: TempDB allocation contention',
            '-- Unknown: Memory grant timeouts'
          ]
        }
      ]
    },
    {
      id: 'azure',
      title: 'Cloud Resource Optimization Through Database Load Testing',
      audience: 'Azure Platforms Team',
      icon: <Cloud className="w-5 h-5" />,
      duration: '16 min',
      keyMessage: 'Potential 30-40% savings on Azure SQL costs through right-sizing',
      color: 'bg-cyan-500',
      slides: [
        {
          title: 'Cloud Cost Optimization',
          type: 'text',
          content: [
            'Current Challenge: Over-provisioned Azure SQL resources due to unknown load patterns',
            'JMeter Gap: Doesn\'t reveal actual database resource consumption patterns',
            'Opportunity: Right-size Azure resources based on real stored procedure load data'
          ]
        },
        {
          title: 'Azure-Specific Benefits',
          type: 'metrics',
          content: [
            'DTU/vCore Optimization: Understand actual compute requirements under load',
            'Auto-scaling Triggers: Set accurate scaling thresholds based on real data',
            'Cost Reduction: Potential 30-40% savings on Azure SQL costs',
            'Performance Predictability: Eliminate performance surprises in cloud'
          ]
        }
      ]
    },
    {
      id: 'qa',
      title: 'Complementing JMeter with Database-Layer Testing',
      audience: 'QA Team',
      icon: <TestTube className="w-5 h-5" />,
      duration: '14 min',
      keyMessage: 'Expand test coverage from 60% to 95% of performance scenarios',
      color: 'bg-red-500',
      slides: [
        {
          title: 'Testing Layer Comparison',
          type: 'comparison',
          content: [
            'JMeter Testing (Current):',
            '├── API Layer ✓',
            '├── HTTP Response Times ✓', 
            '├── Throughput Metrics ✓',
            '└── Database Layer ❌',
            '',
            'Stored Procedure Testing (Proposed):',
            '├── Database Concurrency ✓',
            '├── Resource Contention ✓',
            '├── Deadlock Detection ✓',
            '└── Performance Baselines ✓'
          ]
        },
        {
          title: 'QA Testing Gaps',
          type: 'text',
          content: [
            'JMeter Strength: Validates API contracts and response formats',
            'JMeter Weakness: Cannot simulate database-specific load patterns',
            'Missing Coverage: 80% of production performance issues occur at database layer',
            'Quality Risk: Shipping code that passes API tests but fails under database load'
          ]
        }
      ]
    }
  ];

  const selectedPres = presentations.find(p => p.id === selectedPresentation);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Presentation className="w-5 h-5" />
            Costco HighJump Stress Testing Presentations
          </CardTitle>
          <p className="text-sm text-gray-600">
            Audience-specific presentations addressing JMeter limitations and database stress testing benefits
          </p>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedPresentation} onValueChange={setSelectedPresentation} className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              {presentations.map((pres) => (
                <TabsTrigger key={pres.id} value={pres.id} className="text-xs">
                  {pres.audience.split(' ')[0]}
                </TabsTrigger>
              ))}
            </TabsList>

            {presentations.map((pres) => (
              <TabsContent key={pres.id} value={pres.id} className="space-y-4">
                {/* Presentation Header */}
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 ${pres.color} text-white rounded-lg`}>
                      {pres.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">{pres.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">Target Audience: {pres.audience}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {pres.duration}
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {pres.slides.length} slides
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      <Download className="w-4 h-4 mr-1" />
                      Export
                    </Button>
                    <Button size="sm">
                      <Presentation className="w-4 h-4 mr-1" />
                      Present
                    </Button>
                  </div>
                </div>

                {/* Key Message */}
                <div className="p-4 bg-shiftleft-50 border-l-4 border-shiftleft-500 rounded">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-shiftleft-600" />
                    <span className="font-medium text-shiftleft-900">Key Message</span>
                  </div>
                  <p className="text-shiftleft-800">{pres.keyMessage}</p>
                </div>

                {/* Slides */}
                <div className="space-y-4">
                  {pres.slides.map((slide, index) => (
                    <Card key={index}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">
                            Slide {index + 1}: {slide.title}
                          </CardTitle>
                          <Badge variant="outline">{slide.type}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {slide.type === 'code' ? (
                          <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto">
                            {slide.content.join('\n')}
                          </pre>
                        ) : slide.type === 'comparison' ? (
                          <div className="space-y-2">
                            {slide.content.map((item, i) => (
                              <div key={i} className={`p-2 rounded ${
                                item.includes('Before') || item.includes('Current') || item.includes('JMeter') 
                                  ? 'bg-red-50 border-l-4 border-red-300' 
                                  : item.includes('After') || item.includes('Future') || item.includes('Proposed')
                                  ? 'bg-green-50 border-l-4 border-green-300'
                                  : 'bg-gray-50'
                              }`}>
                                <span className="text-sm">{item}</span>
                              </div>
                            ))}
                          </div>
                        ) : slide.type === 'metrics' ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {slide.content.map((item, i) => (
                              <div key={i} className="flex items-start gap-2 p-3 bg-shiftleft-50 rounded-lg">
                                {item.includes('$') || item.includes('Cost') || item.includes('ROI') ? (
                                  <DollarSign className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                ) : item.includes('%') || item.includes('improvement') ? (
                                  <TrendingUp className="w-4 h-4 text-shiftleft-600 mt-0.5 flex-shrink-0" />
                                ) : (
                                  <Shield className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                                )}
                                <span className="text-sm">{item}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <ul className="space-y-2">
                            {slide.content.map((item, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <div className="w-2 h-2 bg-shiftleft-500 rounded-full mt-2 flex-shrink-0"></div>
                                <span className="text-sm">{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Objection Handling */}
                <Card className="bg-yellow-50 border-yellow-200">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Shield className="w-4 h-4 text-yellow-600" />
                      Common Objections & Responses
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <div className="font-medium text-sm text-red-700 mb-1">
                        "JMeter is sufficient for our testing needs"
                      </div>
                      <div className="text-sm text-gray-700">
                        JMeter tests the API layer excellently, but 80% of performance issues occur at the database layer where JMeter has no visibility. We're not replacing JMeter—we're completing the testing picture.
                      </div>
                    </div>
                    <div>
                      <div className="font-medium text-sm text-red-700 mb-1">
                        "This will slow down our development process"
                      </div>
                      <div className="text-sm text-gray-700">
                        The testing runs in parallel with existing processes and actually accelerates development by catching issues early. The 30-day pilot will demonstrate minimal impact with maximum benefit.
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}